import { Analytics } from '@genoa/analytics'
import { SLCType } from '@genoa/domain'
import { PaymentSchedule } from '@genoa/middle-end'
import { ordinalSuffixFor, toDollars } from '@genoa/utils'
import { Box, Flex } from '@chakra-ui/react'
import styled from '@emotion/styled'

import { useInterestFeeInfoModal, useSecureLineOfCredit } from '../../../../../../hooks'
import { usePropertyPassthroughFee } from '../../../../../../hooks/flex2/pricing/use-property-passthrough-fee'
import { QuestionCircle } from '../../../../../assets'
import { ExtraSmallText, Gap, SmallText } from '../../../../../components'
import { PaymentScheduleItem } from './PaymentScheduleItem'

export interface PaymentItem extends PaymentSchedule {
  amount: number
  amountBeforeFee: number
  day: number
  description?: string
  isChangeDayEnabled?: boolean
  hideQuestionMark?: boolean
  label?: string
  showProcessingFeeDisclaimer?: boolean
}

//** special type to provide compatibility to `EstimatedPaymentSchedule` displays */
type PaymentScheduleEstimatedScheduleBridge = Omit<
  PaymentItem,
  'user_payment_public_id' | 'payment_status' | 'payment_category'
>

export interface PaymentScheduleItemType extends PaymentScheduleEstimatedScheduleBridge {}

export type PaymentScheduleList = PaymentScheduleItemType[]

type PaymentScheduleTableProps = {
  schedule: PaymentScheduleList
  fee: number
  analyticsEvent?: Analytics.Events
  showExtraFees?: boolean
  onRepaymentDayChange?: () => unknown
  onMembershipLearnMore?: () => unknown
  processingFeePercentage?: number
  testID?: string
  showProcessingFee?: boolean
  tableTitle: string
  feeDisclaimerLabel: string
  showAdditionalDepositDisclaimer?: boolean
  showInterestFee?: boolean
  isSimplifiedPaymentsEnabled?: boolean
  isEnabledForSLC?: boolean
  propertyPassthroughFee?: number
}

export const PaymentScheduleTable = (props: PaymentScheduleTableProps) => {
  const { showInterestFeeInfoModal } = useInterestFeeInfoModal()
  const { slcType } = useSecureLineOfCredit()
  const { PropertyPassthroughFeeDisclaimer, displayPropertyPassthroughFee } = usePropertyPassthroughFee(
    props.propertyPassthroughFee
  )
  const shadow = {
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 1,
    },
    shadowOpacity: 0.25,
    shadowRadius: 2,
    elevation: 3,
  }

  if (props.schedule.length === 0) {
    return null
  }

  const getDefaultPaymentLabel = (index: number, payment: PaymentScheduleItemType) => {
    if (payment?.label) {
      return payment.label
    } else {
      if (props.isEnabledForSLC) {
        return 'Payment'
      } else {
        return `${ordinalSuffixFor(index + 1)} payment`
      }
    }
  }

  const displayPropertyPassthroughFeeForItem = (label: string) => {
    if (!slcType && label !== 'Membership fee' && label !== '1st payment' && displayPropertyPassthroughFee) {
      return true
    }
    return false
  }

  const isFlexAnywhere = slcType === SLCType.FLEX_ANYWHERE
  const paymentList = props.isEnabledForSLC ? props.schedule.slice(0, 2) : props.schedule

  const resolvePaymentBreakdown = (payment: PaymentScheduleItemType) => {
    const processingFeePercent = payment.processing_fee_percent ?? 0
    return [
      {
        label: isFlexAnywhere ? 'Payment' : 'Payment subtotal',
        payment: toDollars(payment.base_amount),
        doIndent: props.isEnabledForSLC || isFlexAnywhere,
      },
      {
        label: isFlexAnywhere
          ? `${payment.processing_fee_percent}% monthly rate`
          : `Monthly interest (${payment.processing_fee_percent}%)`,
        payment: toDollars(payment.processing_fee),
        onTooltipPressed: () => showInterestFeeInfoModal(processingFeePercent),
        doIndent: props.isEnabledForSLC || isFlexAnywhere,
      },
    ]
  }

  function getPaymentDescription(payment: PaymentScheduleItemType) {
    return isFlexAnywhere ? `${ordinalSuffixFor(payment.day)} of the month` : undefined
  }

  return (
    <Container>
      <Gap size="spacing_100" />
      <PaymentScheduleBox style={shadow}>
        <Flex flexDirection="column">
          <Flex>
            <TableTitle>{props.tableTitle}</TableTitle>
          </Flex>
          <Divider />

          {paymentList.map((payment, i) => (
            <>
              <PaymentScheduleItem
                payment={toDollars(payment.amount)}
                paymentLabel={getDefaultPaymentLabel(i, payment)}
                description={payment.description || getPaymentDescription(payment)}
                showProcessingFeePercentage={
                  props.isEnabledForSLC && i !== 0 ? props.showInterestFee : payment.showProcessingFeeDisclaimer
                }
                hideChangeDate={!payment.isChangeDayEnabled}
                onChangeDate={props.onRepaymentDayChange}
                paymentBreakdown={props.showInterestFee && i !== 0 ? resolvePaymentBreakdown(payment) : undefined}
                displayPropertyPassthroughFee={displayPropertyPassthroughFeeForItem(getDefaultPaymentLabel(i, payment))}
              />

              {props.showAdditionalDepositDisclaimer && i === 0 && <AdditionalDepositDisclaimer />}

              <Gap size="spacing_100" />
            </>
          ))}
          {Boolean(props.fee) && (
            <Flex justifyContent="space-between">
              <Flex alignItems="center">
                <Circle />
                <Flex flexDirection="column">
                  <Flex alignItems="center">
                    <SmallText>Membership fee</SmallText>
                    <QuestionCircleTooltip onClick={props.onMembershipLearnMore}>
                      <QuestionCircleTooltipLabel>?</QuestionCircleTooltipLabel>
                    </QuestionCircleTooltip>
                  </Flex>
                  <MembershipFeeLabel>15th of the month</MembershipFeeLabel>
                </Flex>
              </Flex>
              <SmallText>${toDollars(props.fee)}</SmallText>
            </Flex>
          )}
        </Flex>
      </PaymentScheduleBox>
      {props.showProcessingFee && (
        <>
          <Gap size="spacing_75" />
          <Flex alignItems="flex-end">
            <ProcessingFeeLabel>{props.feeDisclaimerLabel}</ProcessingFeeLabel>
          </Flex>
        </>
      )}
      {displayPropertyPassthroughFee && !slcType && <PropertyPassthroughFeeDisclaimer />}
      <Gap size="spacing_150" />
    </Container>
  )
}

const Container = styled(Flex)`
  flex-direction: column;
`

const Circle = styled(Flex)`
  align-items: center;
  background-color: ${({ theme }) => theme.colors.jewelPurple};
  border-radius: ${({ theme }) => theme.radii.rounded};
  height: ${({ theme }) => theme.fixedSizes.spacing_50};
  justify-content: center;
  margin-right: ${({ theme }) => theme.fixedSizes.spacing_62_5};
  width: ${({ theme }) => theme.fixedSizes.spacing_50};
`

const PaymentScheduleBox = styled(Box)`
  background-color: ${({ theme }) => theme.colors.wildflower};
  border-color: ${({ theme }) => theme.colors.settings};
  border-radius: ${({ theme }) => theme.radii.xl};
  border-width: ${({ theme }) => theme.fixedSizes.spacing_12_5};
  padding: ${({ theme }) => theme.fixedSizes.spacing_100};
  width: 100%;
`

const Divider = styled(Flex)`
  background-color: ${({ theme }) => theme.colors.cloud};
  height: ${({ theme }) => theme.fixedSizes.spacing_12_5};
  margin-top: ${({ theme }) => theme.fixedSizes.spacing_50};
  margin-bottom: ${({ theme }) => theme.fixedSizes.spacing_75};
  width: 100%;
`

const AdditionalDepositDisclaimerContainer = styled(PaymentScheduleBox)`
  background-color: ${({ theme }) => theme.colors.cloud};
`

const MembershipFeeLabel = styled(ExtraSmallText)`
  color: ${({ theme }) => theme.colors.dusk};
`

const ProcessingFeeLabel = styled(ExtraSmallText)`
  color: ${({ theme }) => theme.colors.dusk};
`

const TableTitle = styled(SmallText)`
  font-weight: ${({ theme }) => theme.fontWeights.bold};
`

export const QuestionCircleTooltip = styled(QuestionCircle)`
  align-items: center;
  background-color: ${({ theme }) => theme.colors.jewelPurple};
  border-radius: ${({ theme }) => theme.radii.rounded};
  height: ${({ theme }) => theme.fixedSizes.spacing_100};
  justify-content: center;
  margin-left: ${({ theme }) => theme.fixedSizes.spacing_50};
  width: ${({ theme }) => theme.fixedSizes.spacing_100};
`

export const QuestionCircleTooltipLabel = styled(ExtraSmallText)`
  color: ${({ theme }) => theme.colors.white};
`

const AdditionalDepositDisclaimer = () => (
  <>
    <Gap size="spacing_100" />
    <AdditionalDepositDisclaimerContainer>
      <ExtraSmallText>
        Your rent is higher this month! We&rsquo;ll need a little extra in your deposit to cover the difference.
      </ExtraSmallText>
    </AdditionalDepositDisclaimerContainer>
  </>
)
